import React from 'react';

const MonthRibbon = ({ 
  currentDate, 
  savedJournals, // Function that returns entries for a month
  getJournalsForMonthByDate, // Function that returns entries grouped by date
  openMonth, 
  setOpenMonth, 
  createNewEntry, 
  openJournal, 
  deleteJournal 
}) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentMonthRef = React.useRef(null);
  React.useEffect(() => {
    currentMonthRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, []);

  // Get current month index
  const getCurrentMonth = () => {
    return currentDate.getMonth();
  };

  // Get journal entries for a specific month (simple version for dots)
  const getJournalsForMonth = (monthIndex) => {
    if (typeof savedJournals === 'function') {
      return savedJournals(monthIndex);
    }
    // Fallback for old structure
    const year = currentDate.getFullYear();
    return Object.values(savedJournals || {}).filter(journal => {
      const journalDate = new Date(journal.createdAt);
      return journalDate.getMonth() === monthIndex && journalDate.getFullYear() === year;
    });
  };

  // Generate color for journal entry based on type
  const getJournalColor = (entry, index) => {
    if (entry.type === 'structured') {
      switch (entry.primaryType) {
        case 'daily': return '#3b82f6'; // Blue
        case 'meeting': return '#10b981'; // Green  
        case 'project': return '#8b5cf6'; // Purple
        default: return '#6b7280'; // Gray
      }
    }
    
    // Default colors for regular entries
    const colors = [
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7',
      '#a0e7e5', '#ffeaa7', '#fab1a0', '#fd79a8', '#fdcb6e'
    ];
    return colors[index % colors.length];
  };

  // Get entry title for display
  const getEntryTitle = (entry) => {
    if (entry.type === 'structured') {
      return entry.title || `${entry.primaryType} Entry`;
    }
    return entry.title || 'Journal Entry';
  };

  // Toggle month dropdown
  const toggleMonth = (index) => {
    setOpenMonth(openMonth === index ? null : index);
  };

  return (
    <div className="glass-month-ribbon">
      {months.map((month, index) => (
        <div
          key={month}
          ref={index === getCurrentMonth() ? currentMonthRef : undefined}
          className={`glass-month-item ${index === getCurrentMonth() ? 'active' : ''} ${openMonth === index ? 'dropdown-open' : ''}`}
          onClick={() => toggleMonth(index)}
        >
          <div className="glass-month-header">
            <div className="glass-month-name">{month}</div>
            {getJournalsForMonth(index).length > 0 && (
              <div className="glass-month-chevron">
                {openMonth === index ? '⌃' : '⌄'}
              </div>
            )}
          </div>
          <div className="glass-journal-lines">
            {getJournalsForMonth(index).map((entry, entryIndex) => (
              <div
                key={entryIndex}
                className={`glass-journal-line ${entry.type === 'structured' ? 'structured-entry' : ''}`}
                style={{ backgroundColor: getJournalColor(entry, entryIndex) }}
                title={getEntryTitle(entry)}
              />
            ))}
          </div>
          
          <div className="glass-month-expanded-content">
            <div className="glass-month-content-inner">
              <div className="glass-month-title">{month} {currentDate.getFullYear()}</div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  createNewEntry();
                }}
                className="glass-tech-create-button"
              >
                + New Entry
              </button>
              {Object.keys(getJournalsForMonthByDate(index)).length > 0 ? (
                Object.entries(getJournalsForMonthByDate(index)).map(([date, entriesForDate]) => (
                  <div key={date} className="glass-tech-date-group">
                    <div className="glass-tech-date-header">{date}</div>
                    <div className="glass-scrollable-journals">
                      {entriesForDate.map((entry) => (
                        <div
                          key={entry.id}
                          className={`glass-tech-journal-item ${entry.type === 'structured' ? 'structured-entry-item' : ''}`}
                          data-type={entry.primaryType}
                          onClick={(e) => {
                            e.stopPropagation();
                            openJournal(entry);
                          }}
                        >
                          <div className="glass-journal-item-content">
                            <div className="glass-tech-journal-title">
                              <span className="glass-entry-icon">{entry.icon || '📝'}</span>
                              <span className="glass-entry-title">{getEntryTitle(entry)}</span>
                              {entry.type === 'structured' && (
                                <span className="glass-entry-type-badge">{entry.primaryType}</span>
                              )}
                            </div>
                            {entry.summary && (
                              <div className="glass-entry-summary">{entry.summary}</div>
                            )}
                            <div className="glass-entry-meta">
                              <span className="glass-entry-date">
                                {new Date(entry.createdAt || entry.date).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              <button
                                className="glass-delete-journal-btn"
                                onClick={(e) => deleteJournal(entry.id, e)}
                                title="Delete this entry"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="glass-no-journals-tech">No entries found</div>
              )}
            </div>
          </div>
        </div>
      ))}

      <style jsx>{`
        .journal-line.structured-entry {
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .structured-entry-item {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-left: 3px solid;
        }

        .structured-entry-item[data-type="daily"] {
          border-left-color: #3b82f6;
        }

        .structured-entry-item[data-type="meeting"] {
          border-left-color: #10b981;
        }

        .structured-entry-item[data-type="project"] {
          border-left-color: #8b5cf6;
        }

        .entry-icon {
          margin-right: 0.5rem;
          font-size: 0.8rem;
        }

        .entry-title {
          flex: 1;
          font-weight: 500;
        }

        .entry-type-badge {
          background: rgba(255, 255, 255, 0.15);
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.6rem;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
          text-transform: uppercase;
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        .entry-summary {
          font-size: 0.6rem;
          color: rgba(255, 255, 255, 0.5);
          margin: 0.2rem 0;
          line-height: 1.2;
          max-height: 2.4em;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .entry-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.5rem;
        }

        .entry-date {
          font-size: 0.6rem;
          color: rgba(255, 255, 255, 0.4);
          font-family: 'JetBrains Mono', monospace;
        }

        .tech-journal-title {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin-bottom: 0.25rem;
        }

        .journal-item-content {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .delete-journal-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.4);
          width: 16px;
          height: 16px;
          border-radius: 50%;
          font-size: 10px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          line-height: 1;
          flex-shrink: 0;
          opacity: 0;
          transform: scale(0.8);
        }

        .tech-journal-item:hover .delete-journal-btn {
          opacity: 1;
          transform: scale(1);
        }

        .delete-journal-btn:hover {
          background: rgba(255, 100, 100, 0.2);
          border-color: rgba(255, 100, 100, 0.3);
          color: rgba(255, 150, 150, 0.9);
        }
      `}</style>
    </div>
  );
};

export default MonthRibbon;